import React, { useState, useEffect, useMemo } from 'react';
import apiService from '../../../../services/shared/apiService';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface TagTrendPanelProps {
  patientId: string;
}

interface TagLog {
  tag: string;
  timestamp: string;
}

const mockTagLogs: TagLog[] = [
  { tag: 'resistance', timestamp: '2024-05-21T10:00:00Z' },
  { tag: 'reflection', timestamp: '2024-05-21T11:00:00Z' },
  { tag: 'trust', timestamp: '2024-05-20T09:00:00Z' },
  { tag: 'resistance', timestamp: '2024-05-19T14:00:00Z' },
  { tag: 'reflection', timestamp: '2024-05-18T10:00:00Z' },
  { tag: 'breakthrough', timestamp: '2024-05-17T15:00:00Z' },
  { tag: 'resistance', timestamp: '2024-05-16T12:00:00Z' },
  { tag: 'trust', timestamp: '2024-05-15T11:00:00Z' },
  { tag: 'reflection', timestamp: '2024-05-14T16:00:00Z' },
];

const TagTrendPanel: React.FC<TagTrendPanelProps> = ({ patientId }) => {
  const [logs, setLogs] = useState<TagLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        // const data = await apiService.getSymbolicTagLogs(patientId);
        // setLogs(data);
        setLogs(mockTagLogs);
      } catch (err) {
        console.error("Using mock data due to error:", err);
        // setError('Failed to load tag logs. Displaying sample data.');
        setLogs(mockTagLogs);
      } finally {
        setIsLoading(false);
      }
    };
    fetchLogs();
  }, [patientId]);
  
  const tagFrequency = useMemo(() => {
    const counts = logs.reduce((acc, log) => {
      acc[log.tag] = (acc[log.tag] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    return Object.entries(counts).sort(([, countA], [, countB]) => countB - countA);
  }, [logs]);

  const chartData = {
    labels: tagFrequency.map(([tag]) => tag),
    datasets: [
      {
        label: 'Tag Frequency',
        data: tagFrequency.map(([, count]) => count),
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1,
      },
    ],
  };
  
  const chartOptions = {
    indexAxis: 'y' as const,
    elements: {
      bar: {
        borderWidth: 2,
      },
    },
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: 'Symbolic Tag Frequency',
      },
    },
  };

  if (isLoading) return <div className="text-center p-4">Loading tag trends...</div>;
  if (error) return <div className="text-center p-4 text-red-500">{error}</div>;

  return (
    <div className="bg-white p-4 rounded-lg shadow">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Symbolic Tag Trends</h2>
      <Bar options={chartOptions} data={chartData} />
    </div>
  );
};

export default TagTrendPanel; 